import React from 'react';
import { Dialog } from 'ui';

import './_practice-mascot-dialog.scss';

/**
 * PracticeMascotDialog - Shared shape of the practice alert dialogs that pair a StudyBuddy
 * mascot with a short message and a SINGLE CTA (TANAI-2087): the "leave a volatile KC practice"
 * confirmation and the "practice could not be generated" notice are the very same dialog with
 * different copy and a different illustration, so the geometry lives here once.
 *
 * Leans on the ui Dialog for the header (title + close X) and the footer CTA, and only lays out
 * the horizontal body (mascot + text). Pure JSX and fully driven by props: every consumer owns
 * its own copy (`title` / `body` / `ctaText`) and its own effects, so this atom never imports
 * translations nor knows what pressing the CTA does. `onSuccess` runs the CTA, `onClose` the
 * header X; both are always offered, and `isLoading` blocks the footer while an action of the
 * consumer is in flight.
 *
 * It is always an `alertdialog`: both consumers either destroy something (the practice) or
 * report a failure the student must acknowledge. A future migration of the non-alert siblings
 * (PendingPracticesDialog) would add the flag then — it is not modelled up front so the atom
 * exposes no unused surface.
 *
 * The illustration is picked with `mascot` and resolved through a per-tenant CSS var
 * (`--tangerine-ai-bot-*`, defined in `public/css/styles.css`), so each tenant swaps the artwork
 * automatically.
 */

/** StudyBuddy illustration shown next to the message. */
export type PracticeMascot = 'disconnected' | 'look-up';

type PracticeMascotDialogProps = {
  body: string;
  ctaText: string;
  isLoading?: boolean;
  isOpen: boolean;
  mascot: PracticeMascot;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
};

function PracticeMascotDialog({
  body,
  ctaText,
  isLoading = false,
  isOpen,
  mascot,
  onClose,
  onSuccess,
  title,
}: Readonly<PracticeMascotDialogProps>): JSX.Element {
  return (
    <Dialog
      buttonTextSuccess={ctaText}
      className={`practice-mascot-dialog practice-mascot-dialog--${mascot}`}
      isAlert
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      size="m"
      title={title}
    >
      <div className="practice-mascot-dialog__content">
        <span aria-hidden="true" className="practice-mascot-dialog__mascot" />
        <p className="practice-mascot-dialog__body">{body}</p>
      </div>
    </Dialog>
  );
}

export default PracticeMascotDialog;
